import { gameLogo } from "./gameLogo";
const ENTER_EASING = "cubic-bezier(0.05, 0.7, 0.1, 1)";
const EXIT_EASING = "cubic-bezier(0.3, 0, 0.8, 0.15)";
const BANNER_Y = 307.883;
/** Coordinates one outgoing/incoming scene; repeated input always wins. */
export class HeroMotion {
    constructor(_players, _banner, _preload, _updateBackdrop) {
        this._players = _players;
        this._banner = _banner;
        this._preload = _preload;
        this._updateBackdrop = _updateBackdrop;
        this._revision = 0;
        this._exiting = false;
        this._transitions = [];
    }
    show(item, animate = true) {
        var _a, _b, _c, _d;
        if (animate && item === this._requested)
            return;
        this._requested = item;
        this._revision += 1;
        const revision = this._revision;
        this._cancelPending();
        if (animate && item === this._current && !this._exiting) {
            this._preload.children = [];
            return;
        }
        if (!animate || !item) {
            this._stopTransitions();
            this._apply(item, (_a = item === null || item === void 0 ? void 0 : item.players.map((player) => this._players.stage.c(player))) !== null && _a !== void 0 ? _a : []);
            this._preload.children = [];
            return;
        }
        const players = item.players.map((player) => this._players.stage.c(player));
        const assets = [
            ...players,
            this._players.stage.c(gameLogo(item.scoreBanner.homeLogoSrc, (_b = item.scoreBanner.homeLogoWidth) !== null && _b !== void 0 ? _b : 149, 149)),
            this._players.stage.c(gameLogo(item.scoreBanner.awayLogoSrc, (_c = item.scoreBanner.awayLogoWidth) !== null && _c !== void 0 ? _c : 149, (_d = item.scoreBanner.awayLogoHeight) !== null && _d !== void 0 ? _d : 149)),
        ];
        // Transparent, active texture users keep these assets resident while they
        // load and while the old scene exits. Detached loadTexture() requests can
        // be reclaimed by Lightning's texture GC before the entrance starts.
        this._preload.children = assets.map((asset) => ({
            w: 1,
            h: 1,
            color: 0x00ffffff,
            texture: asset.texture,
        }));
        let settled = false;
        // Keep the current scene visible while the next artwork loads. Only the
        // latest selection owns this listener, including when a request fails.
        const ready = () => {
            if (revision !== this._revision || !settled)
                return;
            if (assets.some((asset) => { var _a; return !asset.textureIsLoaded() && !((_a = asset.texture) === null || _a === void 0 ? void 0 : _a.loadError); }))
                return;
            this._cancelPending();
            if (this._current) {
                this._exit(item, players, revision);
            }
            else {
                this._enter(item, players);
            }
        };
        this._loadListener = ready;
        this._players.stage.on("frameStart", ready);
        this._selectionTimer = setTimeout(() => {
            settled = true;
            ready();
        }, 90);
    }
    dispose() {
        this._revision += 1;
        this._cancelPending();
        this._stopTransitions();
        this._preload.children = [];
    }
    _apply(item, players) {
        this._current = item;
        this._exiting = false;
        this._players.children = players;
        this._banner.patch(Object.assign({ y: BANNER_Y, alpha: 1, visible: !!item }, (item && {
            item: Object.assign(Object.assign({}, item.scoreBanner), { homeName: item.home.name, awayName: item.away.name, date: item.date, live: item.live }),
        })));
        this._updateBackdrop(item);
    }
    _exit(item, players, revision) {
        // Interrupt entrances at their current values instead of snapping back.
        this._stopTransitions();
        this._exiting = true;
        this._players.children.forEach((player, index) => {
            this._move(player, "x", player.x + (index === 0 ? 56 : -56), 0.16, EXIT_EASING);
            this._move(player, "alpha", 0, 0.16, "linear");
        });
        this._move(this._banner, "y", BANNER_Y - 10, 0.16, EXIT_EASING);
        const enter = () => {
            if (revision !== this._revision)
                return;
            this._cancelPending();
            this._enter(item, players);
        };
        // An interrupted exit may already be invisible. Do not wait for a no-op
        // transition's finish event before showing the newest selection.
        if (this._banner.alpha === 0) {
            enter();
        }
        else {
            this._exitListener = enter;
            this._move(this._banner, "alpha", 0, 0.16, "linear").on("finish", enter);
        }
    }
    _enter(item, players) {
        this._stopTransitions();
        this._apply(item, players);
        players.forEach((player, index) => {
            const { x } = item.players[index];
            const delay = index * 0.07;
            // Array order is paint order: back-most first, front-most last.
            player.patch({ x: x + (index === 0 ? 110 : -140), alpha: 0 });
            this._move(player, "x", x, 0.42, ENTER_EASING, delay);
            this._move(player, "alpha", 1, 0.28, "linear", delay);
        });
        this._banner.patch({ y: BANNER_Y + 16, alpha: 0 });
        const release = () => {
            this._preload.children = [];
            this._banner.transition("y").off("finish", release);
            this._releaseListener = undefined;
        };
        this._releaseListener = release;
        this._move(this._banner, "y", BANNER_Y, 0.32, ENTER_EASING, 0.04).on("finish", release);
        this._move(this._banner, "alpha", 1, 0.24, "linear", 0.04);
    }
    _move(element, property, target, duration, timingFunction, delay = 0) {
        element.setSmooth(property, target, { duration, timingFunction, delay });
        const transition = element.transition(property);
        this._transitions.push(transition);
        return transition;
    }
    _stopTransitions() {
        this._transitions.forEach((transition) => transition.stop());
        this._transitions = [];
    }
    _cancelPending() {
        clearTimeout(this._selectionTimer);
        this._selectionTimer = undefined;
        if (this._loadListener) {
            this._players.stage.off("frameStart", this._loadListener);
            this._loadListener = undefined;
        }
        if (this._exitListener) {
            this._banner.transition("alpha").off("finish", this._exitListener);
            this._exitListener = undefined;
        }
        if (this._releaseListener) {
            this._banner.transition("y").off("finish", this._releaseListener);
            this._releaseListener = undefined;
        }
    }
}
export default HeroMotion;
//# sourceMappingURL=HeroMotion.js.map